import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // PROGRESS BAR: Listen for quantity updates to refresh the bar
    document.addEventListener('cart:updated', this.#handleProgressBarUpdate);

    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // PROGRESS BAR: Clean up the listener
    document.removeEventListener('cart:updated', this.#handleProgressBarUpdate);

    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    // EID RUSH: Reset timer when a new item is added
    localStorage.removeItem('eidRushTimerEnd');

    // PROGRESS BAR: Update the bar immediately when an item is added
    this.#handleProgressBarUpdate();

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * PROGRESS BAR LOGIC:
   * Fetches the updated cart drawer section and replaces the progress bar HTML container.
   */
  #handleProgressBarUpdate = async () => {
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) return;
      
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      const newProgress = html.querySelector('#cart-drawer-progress-container');
      const currentProgress = document.querySelector('#cart-drawer-progress-container');
      
      if (newProgress && currentProgress) {
        currentProgress.innerHTML = newProgress.innerHTML;
      }
    } catch (error) {
      console.error('Failed to update progress bar:', error);
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}


/* =========================================
   EID RUSH TIMER - GLOBAL BACKGROUND LOGIC 
   ========================================= */
if (!window.eidTimerStarted) {
  window.eidTimerStarted = true;

  function clearCart() {
    if (window.isClearingCart) return;
    window.isClearingCart = true;
    
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
    .then(() => {
      localStorage.removeItem('eidRushTimerEnd'); 
      window.location.reload(); 
    })
    .catch(err => {
      console.error('Error clearing cart:', err);
      window.isClearingCart = false;
    });
  }

  function startEidTimer() {
    const duration = 5 * 60 * 1000; // 5 minutes

    setInterval(function() {
      // Find the timer text. If cart is empty, this does nothing and waits quietly.
      const timerDisplays = document.querySelectorAll('.eid-rush-timer-val');
      if (timerDisplays.length === 0) return; 

      let currentEndTime = localStorage.getItem('eidRushTimerEnd');
      
      // If no end time is saved, create a new one!
      if (!currentEndTime) {
        currentEndTime = Date.now() + duration;
        localStorage.setItem('eidRushTimerEnd', currentEndTime);
      }

      const distance = parseInt(currentEndTime) - Date.now();

      if (distance <= 0) {
        timerDisplays.forEach(el => el.textContent = "00:00");
        clearCart(); 
      } else {
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        const formattedTime = minutes + " min " + seconds + " sec";
        
        timerDisplays.forEach(el => el.textContent = formattedTime);
      }
    }, 1000);
  }

  // Start the background check
  startEidTimer();
}